import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
} from '@angular/core';
import {NgClass} from '@angular/common';
import {
    AuditId,
    ProjectId,
    TestObjectId,
} from '@webmate-web-core/shared/types/ids';
import {AuditService, HydratedAuditTarget} from "@webmate-web-core/shared/services/audit.service";
import {faPlus} from "@fortawesome/pro-solid-svg-icons";
import {ModalToolContainerService} from "@webmate-web-core/shared/tools/modaltoolcontainer/modaltoolcontainer.service";
import {
    CreateTestObjectsSourceResult,
    CreateTestObjectsSourceComponent,
    CreateTestObjectsSourceToolConfig
} from '@webmate-web-core/projects/project/auditlab/audits/create-testobjects-source.component';
import {
    ConfigureTestObjectAutomationPoliciesToolComponent,
    ConfigureTestObjectAutomationPoliciesToolConfig
} from '@webmate-web-core/projects/project/auditlab/audits/configure-testobject-automation-policies-tool.component';
import {FontAwesomeModule} from "@fortawesome/angular-fontawesome";
import {WmDirectivesModule} from "@webmate-web-core/shared/directives/directives.module";
import {StateModule} from "@webmate-web-core/shared/components/state/state.module";
import {ResourceViewsModule} from "@webmate-web-core/shared/components/resources/resource-views.module";
import {ButtonModule} from "primeng/button";
import {ProgressBarModule} from "primeng/progressbar";
import {ProgressSpinnerModule} from "primeng/progressspinner";
import {WmNotificationService} from "@webmate-web-core/shared/components/notification/wm-notification.service";
import {StatedataViewerComponent, StatedataViewerToolConfig} from "@webmate-web-core/shared/tools/statedataviewer/statedataviewer.component";
import {TagsModule} from "@webmate-web-core/shared/components/tags/tags.module";
import {Tag} from "@webmate-web-core/shared/types/tag";
import {ScreenshotModule} from "@webmate-web-core/shared/components/screenshot/screenshot.module";
import {TooltipModule} from "primeng/tooltip";
import {FactsModule} from "@webmate-web-core/shared/components/facts/facts.module";
import {Progress} from '@webmate-web-core/shared/types/base-types';
import {
    TestObjectDetailComponent,
    TestObjectDetailUpdate
} from "@webmate-web-core/projects/project/auditlab/audits/test-object-detail.component";
import {
    TestObjectSummaryComponent
} from "@webmate-web-core/projects/project/auditlab/audits/test-object-summary.component";
import {collectRouteParamsFromSnapshot} from '@webmate-web-core/shared/utils/route-access';
import {ActivatedRoute} from '@angular/router';
import {
    ContextObservationTarget,
    deriveObservationEnvironmentFromGenerationSpec,
    StateCollectorJob,
    TestObject,
    TestObjectResolutionJob,
    TestObjectsSourceSpec
} from '@webmate-web-core/shared/types/testmgmt';
import {TestObjectUpdateAction, TestService} from '@webmate-web-core/shared/services/test.service';
import {BrowserSessionService} from '@webmate-web-core/shared/services/browsersession.service';
import {AutomationPolicyConfig} from '@webmate-web-core/shared/types/browsersession';
import {Subscription, timer} from 'rxjs';
import {switchMap, take, takeWhile} from 'rxjs/operators';

@Component({
    selector: 'wm-audit-test-objects',
    templateUrl: './audit-test-objects.component.html',
    styleUrls: ['./audit-test-objects.component.less'],
    imports: [FontAwesomeModule, WmDirectivesModule, StateModule, ResourceViewsModule, ButtonModule, ProgressBarModule, ProgressSpinnerModule, TagsModule, ScreenshotModule, TooltipModule, FactsModule, TestObjectDetailComponent, TestObjectSummaryComponent, NgClass],
    standalone: true
})
export class AuditTestObjectsComponent implements OnChanges, OnDestroy {

    @Input() projectId: ProjectId;
    @Input() testObjects: HydratedAuditTarget[] = [];
    @Input() readOnly: boolean = false;
    @Input() allowToDeleteActivities: boolean = true;
    @Output() testObjectsChange = new EventEmitter<HydratedAuditTarget[]>();
    @Output() onDeleteActivities = new EventEmitter<{testObjectId: TestObjectId, testObjects: HydratedAuditTarget[]}>();

    auditId?: AuditId;

    testObjectsById: { [testObjectId: string]: HydratedAuditTarget } = {};
    canonicalTestObjectsById: Record<TestObjectId, TestObject> = {};
    representativeStateIdByTestObjectId: Record<TestObjectId, string> = {};
    captureJobsByTestObjectId: Record<TestObjectId, StateCollectorJob | undefined> = {};
    selectedTestObjectId?: TestObjectId;
    resolutionJob?: TestObjectResolutionJob;
    private resolutionJobPolling?: Subscription;
    private representativeStateLookup?: Subscription;
    private testObjectDetailsLookup?: Subscription;
    private capturePollingByTestObjectId: Record<TestObjectId, Subscription | undefined> = {};

    constructor(private toolContainerService: ModalToolContainerService,
                private notificationService: WmNotificationService,
                private route: ActivatedRoute,
                private auditService: AuditService,
                private testService: TestService,
                private browserSessionService: BrowserSessionService) {
        let routeParams = collectRouteParamsFromSnapshot(this.route);
        this.auditId = routeParams['auditId'];
    }

    ngOnDestroy() {
        this.stopResolutionPolling();
        this.representativeStateLookup?.unsubscribe();
        this.testObjectDetailsLookup?.unsubscribe();
        Object.keys(this.capturePollingByTestObjectId).forEach(testObjectId => this.stopRepresentativeStateCapturePolling(testObjectId as TestObjectId));
    }


    ngOnChanges() {
        this.testObjectsById = this.testObjects.reduce((acc: any, obj) => {
            acc[obj.testObjectId] = obj;
            return acc;
        }, {});
        this.cleanupStaleTestObjectState(new Set(this.testObjects.map(obj => obj.testObjectId)));
        this.loadRepresentativeStateIds();
        this.loadTestObjectDetails();
        if (this.selectedTestObjectId) {
            this.selectObject(this.selectedTestObjectId);
        } else if (this.testObjects[0]) {
            this.selectObject(this.testObjects[0].testObjectId);
        }
    }

    addTestObjects(testObjects: HydratedAuditTarget[]) {
        this.emitTestObjectsChange(this.testObjects.concat(testObjects));
    }

    updateTestObject(update: TestObjectDetailUpdate) {
        const testObject = update.testObject;
        const updateActions: TestObjectUpdateAction[] = [
            {
                type: 'SetName',
                name: testObject.target.name || testObject.testObjectId,
            },
            {
                type: 'SetDescription',
                description: testObject.target.description,
            }
        ];

        if (update.automationPolicyChanged) {
            updateActions.push({
                type: 'SetAutomationPolicy',
                automationPolicy: update.automationPolicy ?? null,
            });
        }

        this.testService.updateTestObject(testObject.testObjectId, updateActions).subscribe({
            next: () => {
                this.applyLocalTestObjectUpdate(
                    testObject,
                    update.automationPolicyChanged ? update.automationPolicy ?? null : undefined,
                    update.automationPolicyChanged
                );
            },
            error: err => {
                console.error('Failed to update test object', err);
                this.notificationService.errorNotification('Could not update test object.');
            }
        });
    }

    openRemovalDialog(testObjectId: TestObjectId) {
        if (this.allowToDeleteActivities) {
            this.openWithDeleteActivities(testObjectId);
        } else {
            this.openWithoutDeleteActivities(testObjectId);
        }
    }

    openWithDeleteActivities(testObjectId: TestObjectId) {
        this.notificationService.open(`Do you really want to remove this test object? You can choose to keep associated activities`, 'Remove object and activities', () => {
            const testObjects = this.allTestObjectsExcept(testObjectId);
            this.onDeleteActivities.emit({testObjectId, testObjects});
        }, 'Keep associated activities', () => {
            this.emitTestObjectsChange(this.allTestObjectsExcept(testObjectId));
        }, 'Cancel', () => {});
    }

    openWithoutDeleteActivities(testObjectId: TestObjectId) {
        this.notificationService.open(`Do you really want to remove this test object?`, 'Remove test object', () => {
            this.emitTestObjectsChange(this.allTestObjectsExcept(testObjectId));
        }, 'Cancel', () => {});
    }

    allTestObjectsExcept(testObjectId: TestObjectId) {
        return this.testObjects.filter(obj => obj.testObjectId !== testObjectId);
    }

    selectObject(testObjectId: TestObjectId) {
        this.selectedTestObjectId = testObjectId;
    }

    viewState(state: { id: string, layoutTreeId: string }) {
        this.toolContainerService.openTool(StatedataViewerComponent,
            new StatedataViewerToolConfig(state.layoutTreeId, state.id));
    }

    openAddObjects(event: MouseEvent) {
        this.toolContainerService.openTool(CreateTestObjectsSourceComponent, new CreateTestObjectsSourceToolConfig(
            'Add Test Objects',
            this.projectId,
            (sourceSpec: TestObjectsSourceSpec, result?: CreateTestObjectsSourceResult) => {
                this.handleTestObjectSource(sourceSpec, result);
            },
            undefined,
            'configureNew'
        ));
    }

    private handleTestObjectSource(sourceSpec: TestObjectsSourceSpec, result?: CreateTestObjectsSourceResult): void {
        const directlyCreatedIds = result?.directlyCreatedTestObjectIds || [];
        if (directlyCreatedIds.length > 0) {
            this.openPolicyConfigurationForIds(directlyCreatedIds, [], () => this.appendResolvedTestObjects(directlyCreatedIds));
            return;
        }

        this.testService.getTestObjectsInProject$(this.projectId)
            .pipe(take(1))
            .subscribe({
                next: testObjects => {
                    this.startResolvingTestObjects(
                        sourceSpec,
                        new Set((testObjects || []).map(testObject => testObject.id)),
                        true
                    );
                },
                error: err => {
                    console.error('Failed to load project Test Objects before source resolution', err);
                    this.notificationService.errorNotification('Could not prepare Test Object source generation.');
                    this.toolContainerService.closeLastTool();
                }
            });
    }

    private startResolvingTestObjects(sourceSpec: TestObjectsSourceSpec,
                                      preExistingProjectTestObjectIds: Set<string> = new Set(),
                                      configureNewPolicies: boolean = false): void {
        this.stopResolutionPolling();
        this.testService.resolveTestObjects$(this.projectId, sourceSpec).subscribe({
            next: job => {
                this.resolutionJob = job;
                if (!job.id) {
                    this.handleResolvedTestObjects(job.resolvedTestObjectIds || [], preExistingProjectTestObjectIds, configureNewPolicies);
                    return;
                }
                this.pollResolutionJob(job.id, preExistingProjectTestObjectIds, configureNewPolicies);
            },
            error: err => {
                console.error('Failed to start test object resolution', err);
                this.notificationService.errorNotification('Could not start test object source generation.');
                if (configureNewPolicies) {
                    this.toolContainerService.closeLastTool();
                }
            }
        });
    }

    private pollResolutionJob(jobId: string,
                              preExistingProjectTestObjectIds: Set<string>,
                              configureNewPolicies: boolean): void {
        this.resolutionJobPolling = timer(0, 2000).pipe(
            switchMap(() => this.testService.getTestObjectResolutionJob$(this.projectId, jobId)),
            takeWhile(job => !this.isResolutionFinished(job), true)
        ).subscribe({
            next: job => {
                this.resolutionJob = job;
                if (!this.isResolutionFinished(job)) {
                    return;
                }

                this.stopResolutionPolling();

                if (job.progress.failureMsg) {
                    this.notificationService.errorNotification(job.progress.failureMsg);
                    if (configureNewPolicies) {
                        this.resolutionJob = undefined;
                        this.toolContainerService.closeLastTool();
                    }
                    return;
                }

                this.handleResolvedTestObjects(job.resolvedTestObjectIds || [], preExistingProjectTestObjectIds, configureNewPolicies);
            },
            error: err => {
                this.stopResolutionPolling();
                console.error('Failed to poll test object resolution job', err);
                this.notificationService.errorNotification('Could not load test object source generation progress.');
                if (configureNewPolicies) {
                    this.toolContainerService.closeLastTool();
                }
            }
        });
    }

    private appendResolvedTestObjects(testObjectIds: string[]): void {
        const existingIds = new Set(this.testObjects.map(obj => obj.testObjectId));
        const missingIds = testObjectIds.filter(id => !existingIds.has(id));
        if (missingIds.length === 0) {
            return;
        }

        this.testService.getTestObjects$(missingIds).subscribe({
            next: testObjects => {
                const newTestObjects = testObjects.map(testObject => {
                    const environment = deriveObservationEnvironmentFromGenerationSpec(testObject.generationSpec);
                    return {
                        testObjectId: testObject.id,
                        target: {
                            type: 'ContextObservationTarget',
                            name: testObject.name || testObject.id,
                            testObjectId: testObject.id,
                            ...(environment ? {environment} : {}),
                            ...(testObject.description ? {description: testObject.description} : {}),
                            ...((testObject.labels || []).length > 0 ? {labels: [...(testObject.labels || [])]} : {}),
                        } as ContextObservationTarget,
                    } as HydratedAuditTarget;
                });

                this.addTestObjects(newTestObjects);
            },
            error: err => {
                console.error('Failed to load resolved test objects', err);
                this.notificationService.errorNotification('Could not load resolved test objects.');
            }
        });
    }

    private handleResolvedTestObjects(resolvedTestObjectIds: string[],
                                      preExistingProjectTestObjectIds: Set<string>,
                                      configureNewPolicies: boolean): void {
        if (!configureNewPolicies) {
            this.appendResolvedTestObjects(resolvedTestObjectIds);
            this.resolutionJob = undefined;
            return;
        }

        const uniqueResolvedIds = Array.from(new Set(resolvedTestObjectIds));
        const newTestObjectIds = uniqueResolvedIds.filter(testObjectId => !preExistingProjectTestObjectIds.has(testObjectId));
        const existingTestObjectIds = uniqueResolvedIds.filter(testObjectId => preExistingProjectTestObjectIds.has(testObjectId));

        this.openPolicyConfigurationForIds(newTestObjectIds, existingTestObjectIds, () => {
            this.appendResolvedTestObjects(uniqueResolvedIds);
            this.resolutionJob = undefined;
        });
    }

    private openPolicyConfigurationForIds(newTestObjectIds: string[],
                                          existingTestObjectIds: string[],
                                          onDone: () => void): void {
        if (newTestObjectIds.length === 0) {
            onDone();
            this.toolContainerService.closeLastTool();
            return;
        }

        const idsToLoad = Array.from(new Set([...newTestObjectIds, ...existingTestObjectIds]));
        this.testService.getTestObjects$(idsToLoad).pipe(take(1)).subscribe({
            next: testObjects => {
                const testObjectsById = new Map(testObjects.map(testObject => [testObject.id, testObject]));
                const newTestObjects = newTestObjectIds
                    .map(testObjectId => testObjectsById.get(testObjectId))
                    .filter((testObject): testObject is TestObject => !!testObject);
                const existingTestObjects = existingTestObjectIds
                    .map(testObjectId => testObjectsById.get(testObjectId))
                    .filter((testObject): testObject is TestObject => !!testObject);

                if (newTestObjects.length === 0) {
                    onDone();
                    this.toolContainerService.closeLastTool();
                    return;
                }

                this.toolContainerService.openTool(
                    ConfigureTestObjectAutomationPoliciesToolComponent,
                    new ConfigureTestObjectAutomationPoliciesToolConfig(
                        this.projectId,
                        newTestObjects,
                        existingTestObjects,
                        onDone
                    )
                );
            },
            error: err => {
                console.error('Failed to load resolved Test Objects for automation policy configuration', err);
                this.notificationService.errorNotification('Could not load Test Objects for automation policy configuration.');
                onDone();
                this.toolContainerService.closeLastTool();
            }
        });
    }

    private isResolutionFinished(job: TestObjectResolutionJob): boolean {
        return !!job.progress.failureMsg || this.getResolutionFraction(job.progress.progress) >= 1;
    }

    protected hasResolutionTerminalState(job: TestObjectResolutionJob): boolean {
        return this.isResolutionFinished(job);
    }

    protected getResolutionTitle(job: TestObjectResolutionJob): string {
        if (job.progress.failureMsg) {
            return 'Generating test objects failed';
        }
        if (this.isResolutionFinished(job)) {
            return 'Loading resolved Test Objects';
        }
        return 'Generating test objects';
    }

    protected getResolutionMessage(job: TestObjectResolutionJob): string {
        if (job.progress.failureMsg) {
            return job.progress.failureMsg;
        }
        if (this.isResolutionFinished(job)) {
            return 'Applying the resolved test objects to this audit.';
        }
        return job.progress.progressMessage || 'Resolving source...';
    }

    protected getResolutionProgressPercent(job: TestObjectResolutionJob): number {
        return Math.round(this.getResolutionFraction(job.progress.progress) * 100);
    }

    captureRepresentativeState(testObjectId: TestObjectId): void {
        if (!this.isRepresentativeStateCaptureAvailable(testObjectId)) {
            return;
        }

        this.stopRepresentativeStateCapturePolling(testObjectId);
        this.captureJobsByTestObjectId = {
            ...this.captureJobsByTestObjectId,
            [testObjectId]: undefined,
        };

        this.testService.captureRepresentativeState$(testObjectId).subscribe({
            next: jobId => {
                this.pollRepresentativeStateCapture(testObjectId, jobId);
            },
            error: err => {
                console.error('Failed to start representative state capture', err);
                this.notificationService.toastErrorNotification('Could not start representative state capture.');
            }
        });
    }

    protected getCaptureJob(testObjectId: TestObjectId): StateCollectorJob | undefined {
        return this.captureJobsByTestObjectId[testObjectId];
    }

    protected isRepresentativeStateCaptureAvailable(testObjectId: TestObjectId): boolean {
        const generationType = (this.canonicalTestObjectsById[testObjectId]?.generationSpec as any)?.type;
        return generationType === 'URLGenerationSpec' || generationType === 'InteractionPathGenerationSpec';
    }

    protected getRepresentativeStateCaptureUnavailableReason(testObjectId: TestObjectId): string | undefined {
        const testObject = this.canonicalTestObjectsById[testObjectId];
        const generationType = (testObject?.generationSpec as any)?.type;
        if (!generationType) {
            return undefined;
        }
        if (generationType === 'ManualGenerationSpec') {
            return 'Manual Test Objects do not support automatic representative state capture.';
        }
        if (generationType === 'MobileAppGenerationSpec') {
            return undefined;
        }
        if (!this.isRepresentativeStateCaptureAvailable(testObjectId)) {
            return 'Representative state capture is currently available only for URL-based and interaction-path Test Objects.';
        }
        return undefined;
    }

    private getResolutionFraction(progress?: Progress): number {
        const current = progress?.current ?? 0;
        return Math.max(0, Math.min(1, current));
    }

    private stopResolutionPolling(): void {
        this.resolutionJobPolling?.unsubscribe();
        this.resolutionJobPolling = undefined;
    }

    private loadRepresentativeStateIds(afterLoad?: () => void): void {
        this.representativeStateLookup?.unsubscribe();

        const testObjectIds = this.testObjects.map(obj => obj.testObjectId);
        if (!testObjectIds.length) {
            this.representativeStateIdByTestObjectId = {};
            afterLoad?.();
            return;
        }

        this.representativeStateLookup = this.auditService.getRepresentativeStatesByTestObjectIds$(testObjectIds).subscribe({
            next: statesByTestObjectId => {
                this.representativeStateIdByTestObjectId = this.testObjects.reduce((acc, obj) => {
                    const state = statesByTestObjectId[obj.testObjectId];
                    if (state) {
                        acc[obj.testObjectId] = state.id;
                    }
                    return acc;
                }, {} as Record<TestObjectId, string>);
                afterLoad?.();
            },
            error: err => {
                console.error('Failed to load representative states for test objects', err);
                this.representativeStateIdByTestObjectId = {};
                afterLoad?.();
            }
        });
    }

    private loadTestObjectDetails(): void {
        this.testObjectDetailsLookup?.unsubscribe();

        const testObjectIds = this.testObjects.map(obj => obj.testObjectId);
        if (!testObjectIds.length) {
            this.canonicalTestObjectsById = {};
            return;
        }

        this.testObjectDetailsLookup = this.testService.getTestObjects$(testObjectIds).subscribe({
            next: testObjects => {
                this.canonicalTestObjectsById = testObjects.reduce((acc, testObject) => {
                    acc[testObject.id] = testObject;
                    return acc;
                }, {} as Record<TestObjectId, TestObject>);
            },
            error: err => {
                console.error('Failed to load canonical test object details', err);
                this.canonicalTestObjectsById = {};
            }
        });
    }

    setTagsForSelectedTestObject(tags: Tag[]) {
        if (!this.selectedTestObjectId) {
            return;
        }

        const selected = this.testObjectsById[this.selectedTestObjectId];
        if (!selected) {
            return;
        }

        this.testService.updateTestObject(selected.testObjectId, [{
            type: 'SetLabels',
            labels: tags,
        }]).subscribe({
            next: () => {
                this.applyLocalTestObjectUpdate({
                    ...selected,
                    target: {
                        ...selected.target,
                        labels: [...tags],
                    }
                });
            },
            error: err => {
                console.error('Failed to update test object labels', err);
                this.notificationService.errorNotification('Could not update test object labels.');
            }
        });
    }

    private applyLocalTestObjectUpdate(updatedTestObject: HydratedAuditTarget,
                                       automationPolicy?: AutomationPolicyConfig | null,
                                       automationPolicyChanged: boolean = false): void {
        this.testObjects = this.testObjects.map(obj => obj.testObjectId === updatedTestObject.testObjectId ? updatedTestObject : obj);
        this.testObjectsById = this.testObjects.reduce((acc: any, obj) => {
            acc[obj.testObjectId] = obj;
            return acc;
        }, {});

        const canonicalTestObject = this.canonicalTestObjectsById[updatedTestObject.testObjectId];
        if (canonicalTestObject) {
            this.canonicalTestObjectsById = {
                ...this.canonicalTestObjectsById,
                [updatedTestObject.testObjectId]: {
                    ...canonicalTestObject,
                    name: updatedTestObject.target.name || canonicalTestObject.name,
                    description: updatedTestObject.target.description,
                    labels: [...(updatedTestObject.target.labels || [])],
                    ...(automationPolicyChanged ? {automationPolicy} : {}),
                }
            };
        }
    }

    private pollRepresentativeStateCapture(testObjectId: TestObjectId, jobId: string): void {
        this.capturePollingByTestObjectId[testObjectId] = timer(0, 2000).pipe(
            switchMap(() => this.browserSessionService.getStateCollectorJob$(jobId as any)),
            takeWhile(job => job.status === 'running', true)
        ).subscribe({
            next: job => {
                this.captureJobsByTestObjectId = {
                    ...this.captureJobsByTestObjectId,
                    [testObjectId]: job,
                };

                if (job.status === 'running') {
                    return;
                }

                this.stopRepresentativeStateCapturePolling(testObjectId);

                if (job.status === 'failed') {
                    this.notificationService.toastErrorNotification(job.progress.failureMsg || 'Representative state capture failed.');
                    return;
                }

                this.loadRepresentativeStateIds(() => {
                    if (!this.representativeStateIdByTestObjectId[testObjectId]) {
                    this.notificationService.toastErrorNotification('Representative state capture finished without linking a state to the Test Object.');
                        return;
                    }

                    this.notificationService.toastNotification('Representative state captured.');
                });
            },
            error: err => {
                this.stopRepresentativeStateCapturePolling(testObjectId);
                console.error('Failed to poll representative state capture job', err);
                this.notificationService.toastErrorNotification('Could not load representative state capture progress.');
            }
        });
    }

    private stopRepresentativeStateCapturePolling(testObjectId: TestObjectId): void {
        this.capturePollingByTestObjectId[testObjectId]?.unsubscribe();
        delete this.capturePollingByTestObjectId[testObjectId];
    }

    private cleanupStaleTestObjectState(currentIds: Set<TestObjectId>): void {
        Object.keys(this.capturePollingByTestObjectId).forEach(id => {
            if (!currentIds.has(id as TestObjectId)) {
                this.stopRepresentativeStateCapturePolling(id as TestObjectId);
            }
        });

        this.captureJobsByTestObjectId = Object.entries(this.captureJobsByTestObjectId).reduce((acc, [id, job]) => {
            if (currentIds.has(id as TestObjectId)) {
                acc[id as TestObjectId] = job;
            }
            return acc;
        }, {} as Record<TestObjectId, StateCollectorJob | undefined>);
    }

    private emitTestObjectsChange(testObjects: HydratedAuditTarget[]) {
        this.testObjectsChange.emit(testObjects);
    }

    protected readonly faPlus = faPlus;
}
